import Sidebar from '@/components/layout/sidebar';
import StaffPerformanceReport from '@/components/reports/staff-performance-report';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import type { UserWithTeam } from '@shared/schema';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Award,
  CheckCircle,
  Clock,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

function WeightsEditor() {
  const [weights, setWeights] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      const res = await fetch('/api/analytics/score-weights', {
        credentials: 'include',
      });
      if (res.ok) setWeights(await res.json());
    })();
  }, []);
  const onChange = (k: string, v: number) =>
    setWeights((w: any) => ({ ...w, [k]: v }));
  const onSave = async () => {
    setSaving(true);
    await fetch('/api/analytics/score-weights', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(weights),
      credentials: 'include',
    });
    setSaving(false);
  };
  if (!weights) return <div className="text-gray-500">Loading…</div>;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {Object.entries(weights).map(([k, v]) => (
          <label key={k} className="text-sm flex items-center gap-2">
            <span className="w-36 capitalize">{k}</span>
            <input
              type="number"
              className="border rounded px-2 py-1 w-28"
              value={v as number}
              onChange={e => onChange(k, parseInt(e.target.value, 10))}
            />
          </label>
        ))}
      </div>
      <Button onClick={onSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save weights'}
      </Button>
    </div>
  );
}

function MyRank({ windowDays }: { windowDays: number }) {
  const [rank, setRank] = useState<{ rank: number; total: number } | null>(
    null
  );
  useEffect(() => {
    (async () => {
      const res = await fetch(
        `/api/analytics/my-ranking?windowDays=${windowDays}`,
        { credentials: 'include' }
      );
      if (res.ok) setRank(await res.json());
    })();
  }, [windowDays]);
  if (!rank) return <div className="text-gray-500">Loading…</div>;
  return (
    <div className="text-2xl font-semibold">
      #{rank.rank} of {rank.total}
    </div>
  );
}

function MyTrend({
  userId,
  windowDays,
}: {
  userId: string;
  windowDays: number;
}) {
  const [data, setData] = useState<{ capturedAt: string; points: number }[]>(
    []
  );
  useEffect(() => {
    (async () => {
      const res = await fetch(
        `/api/analytics/user-score-snapshots/${userId}?windowDays=${windowDays}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const js = await res.json();
        setData(
          js.map((d: any) => ({
            capturedAt: new Date(d.capturedAt).toLocaleDateString(),
            points: d.points,
          }))
        );
      }
    })();
  }, [userId, windowDays]);
  if (!data.length)
    return <div className="text-gray-500">No snapshots yet</div>;
  return (
    <div style={{ width: '100%', height: 200 }}>
      <ResponsiveContainer>
        <LineChart data={data}>
          <XAxis dataKey="capturedAt" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip />
          <Line
            type="monotone"
            dataKey="points"
            stroke="#6366f1"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function MyAssignerBreakdown() {
  const [windowDaysLocal, setWindowDaysLocal] = useState<number>(30);
  const params = new URLSearchParams();
  if (windowDaysLocal) params.set('windowDays', String(windowDaysLocal));
  const { data, isLoading } = useQuery({
    queryKey: ['/api/analytics/assigner-breakdown', windowDaysLocal],
    retry: false,
    queryFn: async () => {
      const res = await fetch(
        `/api/analytics/assigner-breakdown?${params.toString()}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed assigner breakdown');
      return res.json();
    },
  });
  if (isLoading) return <div className="text-sm text-gray-500">Loading…</div>;
  const rows = (data?.items || []) as any[];
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <label className="flex items-center gap-2">
          Window
          <select
            className="border rounded px-2 py-1"
            value={windowDaysLocal}
            onChange={e => setWindowDaysLocal(parseInt(e.target.value, 10))}
          >
            <option value={7}>7</option>
            <option value={30}>30</option>
            <option value={90}>90</option>
            <option value={0}>All</option>
          </select>
          <span>days</span>
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full border text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-2 border">Assigner</th>
              <th className="text-right p-2 border">Total</th>
              <th className="text-right p-2 border">Pending</th>
              <th className="text-right p-2 border">Completed</th>
              <th className="text-right p-2 border">On Time</th>
              <th className="text-right p-2 border">Late</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.assigner.id} className="hover:bg-gray-50">
                <td className="p-2 border whitespace-nowrap">
                  {r.assigner.firstName} {r.assigner.lastName}
                </td>
                <td className="p-2 border text-right">{r.total}</td>
                <td className="p-2 border text-right">{r.pending}</td>
                <td className="p-2 border text-right">{r.completed}</td>
                <td className="p-2 border text-right">{r.onTime}</td>
                <td className="p-2 border text-right">{r.late}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AssigneeStats() {
  const { user } = useAuth();
  const [sortKey, setSortKey] = useState<
    'total' | 'inProgress' | 'overdue' | 'completed'
  >('total');
  const [projectId, setProjectId] = useState<string>('');
  const [teamId, setTeamId] = useState<string>('');
  const [windowDaysLocal, setWindowDaysLocal] = useState<number>(30);
  const { data: projects = [] } = useQuery({
    queryKey: ['/api/projects'],
    retry: false,
  });
  const { data: teams = [] } = useQuery({
    queryKey: ['/api/teams'],
    retry: false,
    enabled: user?.role !== 'team_member',
  });
  const params = new URLSearchParams();
  if (projectId) params.set('projectId', projectId);
  if (teamId) params.set('teamId', teamId);
  if (windowDaysLocal) params.set('windowDays', String(windowDaysLocal));
  const { data, isLoading } = useQuery({
    queryKey: [
      '/api/analytics/assignee-stats',
      projectId,
      teamId,
      windowDaysLocal,
    ],
    retry: false,
    queryFn: async () => {
      const res = await fetch(
        `/api/analytics/assignee-stats?${params.toString()}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed assignee stats');
      return res.json();
    },
  });
  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading…</div>;
  }
  const rows = Array.isArray(data) ? data : [];
  const sorted = [...rows].sort((a: any, b: any) => b[sortKey] - a[sortKey]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          Sort
          <select
            className="border rounded px-2 py-1"
            value={sortKey}
            onChange={e => setSortKey(e.target.value as any)}
          >
            <option value="total">Total</option>
            <option value="inProgress">In Progress</option>
            <option value="overdue">Overdue</option>
            <option value="completed">Completed</option>
          </select>
        </label>
        <label className="flex items-center gap-2">
          Window
          <select
            className="border rounded px-2 py-1"
            value={windowDaysLocal}
            onChange={e => setWindowDaysLocal(parseInt(e.target.value, 10))}
          >
            <option value={7}>7</option>
            <option value={30}>30</option>
            <option value={90}>90</option>
            <option value={0}>All</option>
          </select>
          <span>days</span>
        </label>
        <label className="flex items-center gap-2">
          Project
          <select
            className="border rounded px-2 py-1"
            value={projectId}
            onChange={e => setProjectId(e.target.value)}
          >
            <option value="">All</option>
            {(projects || [])
              .sort((a: any, b: any) => a.name.localeCompare(b.name))
              .map((p: any) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>
        {user?.role !== 'team_member' && (
          <label className="flex items-center gap-2">
            Team
            <select
              className="border rounded px-2 py-1"
              value={teamId}
              onChange={e => setTeamId(e.target.value)}
            >
              <option value="">All</option>
              {(teams || []).map((t: any) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <a
          className="ml-auto text-xs underline text-blue-600"
          href={`/api/analytics/assignee-stats.csv?${params.toString()}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Export CSV
        </a>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full border text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-2 border">Assignee</th>
              <th className="text-right p-2 border">Total</th>
              <th className="text-right p-2 border">In Progress</th>
              <th className="text-right p-2 border">Overdue</th>
              <th className="text-right p-2 border">Completed</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r: any) => (
              <tr key={r.user.id} className="hover:bg-gray-50">
                <td className="p-2 border whitespace-nowrap">
                  {r.user.firstName} {r.user.lastName}
                  {r.user.team?.name ? (
                    <span className="text-xs text-gray-500">
                      {' '}
                      ({r.user.team.name})
                    </span>
                  ) : null}
                </td>
                <td className="p-2 border text-right">{r.total}</td>
                <td className="p-2 border text-right">{r.inProgress}</td>
                <td className="p-2 border text-right">{r.overdue}</td>
                <td className="p-2 border text-right">{r.completed}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();

  // Sidebar collapse state (shared key with board)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  useEffect(() => {
    if (!user) return;
    const key = `sb_sidebar_collapsed_${user.id}`;
    const stored = window.localStorage.getItem(key);
    if (stored === 'true' || stored === 'false') {
      setIsSidebarCollapsed(stored === 'true');
    }
  }, [user?.id]);
  useEffect(() => {
    if (!user) return;
    const key = `sb_sidebar_collapsed_${user.id}`;
    window.localStorage.setItem(key, String(isSidebarCollapsed));
  }, [isSidebarCollapsed, user?.id]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      toast({
        title: 'Unauthorized',
        description: 'You are logged out. Logging in again...',
        variant: 'destructive',
      });
      setTimeout(() => {
        window.location.href = '/api/login';
      }, 500);
    }
  }, [user, authLoading, toast]);

  const { data: users = [], isLoading: usersLoading } = useQuery<
    UserWithTeam[]
  >({
    queryKey: ['/api/users'],
    retry: false,
    enabled: user?.role === 'manager' || user?.role === 'admin',
  });

  // Allow managers/admins to toggle between global and personal stats
  const [statsPersonal, setStatsPersonal] = useState(false);

  // Persist dashboard stats scope per user
  useEffect(() => {
    if (!user) return;
    const key = `sb_dashboard_personal_${user.id}`;
    const stored = window.localStorage.getItem(key);
    if (stored === 'true' || stored === 'false') {
      setStatsPersonal(stored === 'true');
    }
  }, [user?.id]);
  useEffect(() => {
    if (!user) return;
    const key = `sb_dashboard_personal_${user.id}`;
    window.localStorage.setItem(key, String(statsPersonal));
  }, [statsPersonal, user?.id]);

  // Build a stats path that scopes appropriately based on role and toggle
  const statsPath = useMemo(() => {
    if (!user) return '/api/analytics/task-stats';
    if (user.role === 'team_member')
      return `/api/analytics/task-stats?userId=${user.id}`;
    if (user.role === 'manager') {
      return statsPersonal
        ? `/api/analytics/task-stats?userId=${user.id}`
        : `/api/analytics/task-stats?scope=team`;
    }
    if (user.role === 'admin' && statsPersonal)
      return `/api/analytics/task-stats?userId=${user.id}`;
    return '/api/analytics/task-stats';
  }, [user, statsPersonal]);

  // Dashboard window selector (days) and leaderboard/admin scope state
  const [windowDays, setWindowDays] = useState<number>(30);
  const [lbSort, setLbSort] = useState<'asc' | 'desc'>('desc');
  const [adminTeamScope, setAdminTeamScope] = useState<string | 'global'>(
    'global'
  );

  const { data: taskStats } = useQuery({
    queryKey: [statsPath],
    retry: false,
    enabled: !!user,
  });

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data: userPerformance } = useQuery({
    queryKey: ['/api/analytics/user-performance', selectedUserId],
    retry: false,
    enabled:
      !!selectedUserId && (user?.role === 'manager' || user?.role === 'admin'),
  });

  const { data: userScore } = useQuery({
    queryKey: ['/api/analytics/user-scorecard', selectedUserId, windowDays],
    retry: false,
    enabled: !!selectedUserId,
    queryFn: async () => {
      const res = await fetch(
        `/api/analytics/user-scorecard/${selectedUserId}?windowDays=${windowDays}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed scorecard');
      return res.json();
    },
  });

  if (authLoading) {
    return (
      <div className="flex h-screen">
        <Sidebar
          collapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(prev => !prev)}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  const canViewPerformance = user?.role === 'manager' || user?.role === 'admin';

  // Teams for admin scope selector
  const { data: teams = [] } = useQuery({
    queryKey: ['/api/teams'],
    retry: false,
    enabled: user?.role === 'admin' || user?.role === 'manager',
  });

  // My Rank tile also for manager/admin
  const { data: myRank } = useQuery({
    queryKey: ['/api/analytics/my-ranking', windowDays, adminTeamScope],
    retry: false,
    enabled: !!user,
    queryFn: async () => {
      const params = new URLSearchParams({ windowDays: String(windowDays) });
      if (user?.role === 'admin' && adminTeamScope !== 'global')
        params.set('teamId', adminTeamScope);
      const res = await fetch(
        `/api/analytics/my-ranking?${params.toString()}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed my rank');
      return res.json();
    },
  });

  const { data: leaderboard } = useQuery({
    queryKey: [
      '/api/analytics/leaderboard',
      windowDays,
      lbSort,
      adminTeamScope,
    ],
    retry: false,
    enabled: canViewPerformance,
    queryFn: async () => {
      const params = new URLSearchParams({
        windowDays: String(windowDays),
        sort: lbSort,
      });
      if (user?.role === 'admin' && adminTeamScope !== 'global')
        params.set('teamId', adminTeamScope);
      const res = await fetch(
        `/api/analytics/leaderboard?${params.toString()}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed leaderboard');
      return res.json();
    },
  });

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar
        collapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(prev => !prev)}
      />

      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-gray-600">Window:</span>
              <select
                className="border rounded px-2 py-1"
                value={windowDays}
                onChange={e => setWindowDays(parseInt(e.target.value, 10))}
                aria-label="Window days"
              >
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>
            <div>
              <h1
                className="text-3xl font-bold text-gray-900"
                data-testid="text-page-title"
              >
                Performance Dashboard
              </h1>
              <p className="text-gray-600 mt-2">
                Monitor team productivity and task completion metrics
              </p>
            </div>

            {/* Manager/Admin stats scope toggle */}
            {(user?.role === 'manager' || user?.role === 'admin') && (
              <div className="flex items-center gap-2">
                <span
                  className="text-sm text-gray-600"
                  title="Choose whether metrics show all visible tasks or just your own"
                >
                  Stats scope:
                </span>
                <div className="inline-flex rounded-md shadow-sm" role="group">
                  <button
                    type="button"
                    className={`px-3 py-1.5 text-sm font-medium border ${statsPath.includes('userId=') ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50' : 'bg-blue-600 text-white border-blue-600'}`}
                    onClick={() => setStatsPersonal(false)}
                    title="All: show stats for all visible tasks"
                    aria-label="Show stats for all visible tasks"
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1.5 text-sm font-medium border -ml-px ${statsPath.includes('userId=') ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                    onClick={() => setStatsPersonal(true)}
                    title="Me: show stats for my tasks (including help tasks)"
                    aria-label="Show stats for my tasks (including help tasks)"
                  >
                    Me
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* My Rank (all roles) */}
          {myRank && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>My Rank</CardTitle>
                <CardDescription>Last {windowDays} days</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">
                  #{myRank.rank} of {myRank.total}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Overall Stats */}
          {taskStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Total Tasks
                  </CardTitle>
                  <Target className="h-4 w-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div
                    className="text-2xl font-bold"
                    data-testid="metric-total-tasks"
                  >
                    {taskStats.total}
                  </div>
                  <p className="text-xs text-gray-600">Across all projects</p>
                </CardContent>
              </Card>

              <Card
                className="cursor-pointer"
                onClick={() => {
                  window.location.href = '/board?filter=completed';
                }}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Completed
                  </CardTitle>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div
                    className="text-2xl font-bold text-green-600"
                    data-testid="metric-completed-tasks"
                  >
                    {taskStats.completed}
                  </div>
                  <p className="text-xs text-gray-600">
                    {taskStats.total > 0
                      ? Math.round(
                          (taskStats.completed / taskStats.total) * 100
                        )
                      : 0}
                    % completion rate
                  </p>
                </CardContent>
              </Card>

              <Card
                className="cursor-pointer"
                onClick={() => {
                  window.location.href = '/board?filter=urgent';
                }}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Urgent Tasks
                  </CardTitle>
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                </CardHeader>
                <CardContent>
                  <div
                    className="text-2xl font-bold text-red-600"
                    data-testid="metric-urgent-tasks"
                  >
                    {taskStats.urgent}
                  </div>
                  <p className="text-xs text-gray-600">
                    Require immediate attention
                  </p>
                </CardContent>
              </Card>

              {/* Due Soon tile (clickable) */}
              <Card
                className="cursor-pointer"
                onClick={() => {
                  window.location.href = '/board?filter=due-soon';
                }}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    Due Soon
                  </CardTitle>
                  <Clock className="h-4 w-4 text-orange-600" />
                </CardHeader>
                <CardContent>
                  <div
                    className="text-2xl font-bold text-orange-600"
                    data-testid="metric-due-soon-tasks"
                  >
                    {taskStats.dueSoon}
                  </div>
                  <p className="text-xs text-gray-600">
                    Tasks approaching deadline
                  </p>
                </CardContent>
              </Card>

              {/* In Progress tile (clickable) */}
              <Card
                className="cursor-pointer"
                onClick={() => {
                  window.location.href = '/board?filter=in-progress';
                }}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    In Progress
                  </CardTitle>
                  <Clock className="h-4 w-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div
                    className="text-2xl font-bold text-blue-600"
                    data-testid="metric-in-progress-tasks"
                  >
                    {taskStats.inProgress}
                  </div>
                  <p className="text-xs text-gray-600">
                    Currently being worked on
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Assignee-wise analytics */}
          <Card className="mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="space-y-1">
                <CardTitle>Assignee-wise Analytics</CardTitle>
                <CardDescription>
                  Task distribution by assignee
                  {user?.role === 'admin' ? '' : ' (within your visibility)'}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <AssigneeStats />
            </CardContent>
          </Card>

          {/* Leaderboard (Manager/Admin) */}
          {leaderboard && canViewPerformance && (
            <Card className="mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="space-y-1">
                  <CardTitle>Top Performers (last {windowDays} days)</CardTitle>
                  <CardDescription>Based on efficiency score</CardDescription>
                  <div className="flex items-center gap-3 text-sm">
                    <label className="flex items-center gap-2">
                      Sort
                      <select
                        className="border rounded px-2 py-1"
                        value={lbSort}
                        onChange={e => setLbSort(e.target.value as any)}
                      >
                        <option value="desc">High to low</option>
                        <option value="asc">Low to high</option>
                      </select>
                    </label>
                    {user?.role === 'admin' && (
                      <label className="flex items-center gap-2">
                        Scope
                        <select
                          className="border rounded px-2 py-1"
                          value={adminTeamScope}
                          onChange={e =>
                            setAdminTeamScope(e.target.value as any)
                          }
                        >
                          <option value="global">Global</option>
                          {(teams || []).map((t: any) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                </div>
                <Button
                  asChild
                  variant="outline"
                  aria-label="Export leaderboard CSV"
                >
                  <a
                    href={`/api/analytics/leaderboard.csv?windowDays=${windowDays}&sort=${lbSort}${user?.role === 'admin' && adminTeamScope !== 'global' ? `&teamId=${adminTeamScope}` : ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Export CSV
                  </a>
                </Button>
              </CardHeader>
              <CardContent>
                {leaderboard.length === 0 ? (
                  <p className="text-gray-500">No data</p>
                ) : (
                  <ol className="space-y-2">
                    {leaderboard.map((row: any, idx: number) => (
                      <li
                        key={row.user.id}
                        className="flex items-center justify-between border rounded p-2"
                      >
                        <div>
                          <span className="font-medium mr-2">#{idx + 1}</span>
                          {row.user.firstName} {row.user.lastName}
                          {row.user.team?.name ? (
                            <span className="ml-2 text-xs text-gray-600">
                              ({row.user.team.name})
                            </span>
                          ) : null}
                        </div>
                        <div className="text-indigo-700 font-semibold">
                          {row.points}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </CardContent>
            </Card>
          )}

          {/* Staff Performance Section */}
          {canViewPerformance && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Staff List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    Team Members
                  </CardTitle>
                  <CardDescription>
                    Select a team member to view their performance metrics
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {usersLoading ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      </div>
                    ) : users.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">
                        No team members found
                      </p>
                    ) : (
                      users.map(member => (
                        <div
                          key={member.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedUserId === member.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setSelectedUserId(member.id)}
                          data-testid={`staff-card-${member.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-gray-900">
                                {member.firstName} {member.lastName}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {member.team?.name || 'No team assigned'}
                              </p>
                            </div>
                            <Badge
                              variant={
                                member.role === 'admin'
                                  ? 'destructive'
                                  : member.role === 'manager'
                                    ? 'default'
                                    : 'secondary'
                              }
                            >
                              {member.role.replace('_', ' ')}
                            </Badge>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Performance Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Performance Metrics
                  </CardTitle>
                  <CardDescription>
                    {selectedUserId
                      ? `Performance data for selected team member`
                      : 'Select a team member to view performance metrics'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!selectedUserId ? (
                    <div className="text-center py-8 text-gray-500">
                      <Award className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>
                        Select a team member from the list to view their
                        performance metrics
                      </p>
                    </div>
                  ) : userPerformance ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <div
                            className="text-2xl font-bold text-blue-600"
                            data-testid="metric-tasks-given"
                          >
                            {userPerformance.tasksGiven}
                          </div>
                          <div className="text-sm text-blue-600">
                            Tasks Given
                          </div>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <div
                            className="text-2xl font-bold text-green-600"
                            data-testid="metric-tasks-completed"
                          >
                            {userPerformance.tasksCompleted}
                          </div>
                          <div className="text-sm text-green-600">
                            Completed
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-emerald-50 rounded-lg">
                          <div
                            className="text-2xl font-bold text-emerald-600"
                            data-testid="metric-tasks-on-time"
                          >
                            {userPerformance.tasksCompletedOnTime}
                          </div>
                          <div className="text-sm text-emerald-600">
                            On Time
                          </div>
                        </div>
                        <div className="text-center p-3 bg-orange-50 rounded-lg">
                          <div
                            className="text-2xl font-bold text-orange-600"
                            data-testid="metric-tasks-reassigned"
                          >
                            {userPerformance.tasksReassigned}
                          </div>
                          <div className="text-sm text-orange-600">
                            Reassigned
                          </div>
                        </div>
                      </div>

                      {userScore && (
                        <div className="p-3 bg-indigo-50 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2">
                            Efficiency Score (last {userScore.windowDays} days)
                          </h4>
                          <div className="flex items-center gap-4">
                            <div
                              className="text-3xl font-bold text-indigo-700"
                              data-testid="metric-efficiency-score"
                            >
                              {userScore.points}
                            </div>
                            <ul className="text-sm text-gray-700">
                              <li>
                                +10 x Completed approved:{' '}
                                {userScore.breakdown.completedApproved}
                              </li>
                              <li>
                                +5 x Completed on time:{' '}
                                {userScore.breakdown.completedOnTime}
                              </li>
                              <li>
                                +1 x Comments: {userScore.breakdown.comments}
                              </li>
                              <li>
                                +3 x Help provided:{' '}
                                {userScore.breakdown.helpProvided}
                              </li>
                              <li>
                                - Penalties:{' '}
                                {userScore.breakdown.overduePenalties}
                              </li>
                            </ul>
                          </div>
                        </div>
                      )}

                      {userPerformance.tasksGiven > 0 && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2">
                            Success Rate
                          </h4>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-600 h-2 rounded-full"
                              style={{
                                width: `${(userPerformance.tasksCompleted / userPerformance.tasksGiven) * 100}%`,
                              }}
                            ></div>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {Math.round(
                              (userPerformance.tasksCompleted /
                                userPerformance.tasksGiven) *
                                100
                            )}
                            % completion rate
                          </p>
                        </div>
                      )}

                      {userPerformance.tasksCompleted > 0 && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-2">
                            On-Time Delivery
                          </h4>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-emerald-600 h-2 rounded-full"
                              style={{
                                width: `${(userPerformance.tasksCompletedOnTime / userPerformance.tasksCompleted) * 100}%`,
                              }}
                            ></div>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {Math.round(
                              (userPerformance.tasksCompletedOnTime /
                                userPerformance.tasksCompleted) *
                                100
                            )}
                            % on-time delivery
                          </p>
                        </div>
                      )}
                      <div className="mt-3">
                        <Button
                          asChild
                          variant="outline"
                          aria-label="Export user scorecard CSV"
                        >
                          <a
                            href={`/api/analytics/user-scorecard/${selectedUserId}.csv?windowDays=${windowDays}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Export CSV
                          </a>
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-500">
                        Loading performance data...
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Staff Personal Performance Report - Only for team members */}
          {user?.role === 'team_member' && (
            <div className="mt-8 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>My Rank</CardTitle>
                    <CardDescription>
                      Within my team (last {windowDays} days)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <MyRank windowDays={windowDays} />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>My Efficiency Trend</CardTitle>
                    <CardDescription>
                      Snapshots (last {windowDays} days)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <MyTrend userId={user.id} windowDays={windowDays} />
                  </CardContent>
                </Card>
              </div>

              {/* Assigner breakdown for my tasks */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Assigned By (My Tasks)</CardTitle>
                    <CardDescription>
                      Who assigned tasks to me (breakdown)
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent>
                  <MyAssignerBreakdown />
                </CardContent>
              </Card>

              <h2 className="text-2xl font-bold text-gray-900">
                My Performance
              </h2>
              <StaffPerformanceReport user={user} />
            </div>
          )}

          {/* Non-manager view */}
          {!canViewPerformance && user?.role !== 'team_member' && (
            <Card>
              <CardHeader>
                <CardTitle>Access Restricted</CardTitle>
                <CardDescription>
                  Performance metrics are only available to managers and
                  administrators.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">
                    Contact your manager to access detailed performance
                    analytics.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Admin Weights Editor */}
          {user?.role === 'admin' && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Scoring Weights</CardTitle>
                <CardDescription>
                  Adjust how efficiency points are calculated
                </CardDescription>
              </CardHeader>
              <CardContent>
                <WeightsEditor />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
